import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import "@/App.css";

import Home from "@/pages/Home";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import TeamDetail from "@/pages/TeamDetail";
import PlayerDetail from "@/pages/PlayerDetail";
import Standings from "@/pages/Standings";
import Sponsors from "@/pages/Sponsors";
import LiveScorecard from "@/pages/LiveScorecard";
import AdminDetail from "@/pages/AdminDetail";
import Admin from "@/pages/Admin";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import RegisterTeam from "@/pages/RegisterTeam";
import SignupCompany from "@/pages/SignupCompany";
import SignupOrganiser from "@/pages/SignupOrganiser";
import CorporateServices from "@/pages/CorporateServices";
import PackageCustomiser from "@/pages/PackageCustomiser";
import { MyRFQsList, RFQDetail } from "@/pages/MyRFQs";
import Dashboard from "@/pages/Dashboard";
import Bookings from "@/pages/Bookings";
import PlatformAdmin from "@/pages/PlatformAdmin";
import PlayerSignup from "@/pages/PlayerSignup";
import PlayerLogin from "@/pages/PlayerLogin";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PlayerProfile from "@/pages/PlayerProfile";
import { PlayerSearch, PlayerProfileView } from "@/pages/PlayerDirectory";
import VendorSignup from "@/pages/VendorSignup";
import VendorDashboard from "@/pages/VendorDashboard";
import VendorMarket from "@/pages/VendorMarket";
import VendorListingDetail from "@/pages/VendorListingDetail";
import SignupSponsor from "@/pages/SignupSponsor";
import SponsorProfile from "@/pages/SponsorProfile";
import SponsorshipMarketplace from "@/pages/SponsorshipMarketplace";
import About from "@/pages/About";
import ScorerDashboard from "@/pages/ScorerDashboard";
import MyMemberships from "@/pages/MyMemberships";
import PlayerCheckIn from "@/pages/PlayerCheckIn";
import WelcomeModal from "@/components/WelcomeModal";
import { installGlobalImageHealer } from "@/lib/imageUrl";
import Contact from "@/pages/Contact";

// One-shot install: every <img> on every page now self-heals broken URLs (legacy
// preview-hostname absolute URLs are rewritten to the current backend; 404s fall back
// to a neutral placeholder so the broken-image icon never appears).
installGlobalImageHealer();

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <WelcomeModal />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/events" element={<Events />} />
          <Route path="/events/:id" element={<EventDetail />} />
          <Route path="/live/:fixture_id" element={<LiveScorecard />} />
          <Route path="/platform-admin/vendors/:id" element={<AdminDetail type="vendor" />} />
          <Route path="/platform-admin/companies/:id" element={<AdminDetail type="company" />} />
          <Route path="/platform-admin/players/:id" element={<AdminDetail type="player" />} />
          <Route path="/platform-admin/overview" element={<Navigate to="/platform-admin" replace />} />
          <Route path="/vendor/overview" element={<Navigate to="/vendor/dashboard" replace />} />
          <Route path="/teams/:id" element={<TeamDetail />} />
          <Route path="/team-players/:id" element={<PlayerDetail />} />
          <Route path="/standings" element={<Standings />} />
          <Route path="/sponsors" element={<Sponsors />} />
          {/* Legacy /services route → auto-redirects HR/Organiser to the new
              Corporate Services module; other roles hit 404 further down. */}
          <Route path="/services" element={<Navigate to="/corporate-services" replace />} />
          <Route path="/services/:id" element={<Navigate to="/corporate-services" replace />} />
          <Route path="/corporate-services" element={<CorporateServices />} />
          <Route path="/corporate-services/customize/:packageId" element={<PackageCustomiser />} />
          <Route path="/rfqs" element={<MyRFQsList />} />
          <Route path="/rfqs/:rfqId" element={<RFQDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/hire" element={<VendorMarket />} />
          <Route path="/vendor-listing/:id" element={<VendorListingDetail />} />
          <Route path="/my-memberships" element={<MyMemberships />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/bookings" element={<Bookings />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/platform-admin" element={<PlatformAdmin />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/register" element={<Register />} />
          <Route path="/register-team" element={<RegisterTeam />} />
          <Route path="/signup-company" element={<SignupCompany />} />
          <Route path="/signup-organiser" element={<SignupOrganiser />} />

          {/* Players */}
          <Route path="/players/signup" element={<PlayerSignup />} />
          <Route path="/players/login" element={<PlayerLogin />} />
          <Route path="/players/forgot-password" element={<ForgotPassword />} />
          <Route path="/players/reset-password" element={<ResetPassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/players/me" element={<PlayerProfile />} />
          <Route path="/players/check-in" element={<PlayerCheckIn />} />
          <Route path="/players/profiles" element={<PlayerSearch />} />
          <Route path="/players/profiles/:id" element={<PlayerProfileView />} />
          <Route path="/p/:slug" element={<PlayerProfileView />} />

          {/* Vendors */}
          <Route path="/vendor/signup" element={<VendorSignup />} />
          <Route path="/vendor/dashboard" element={<VendorDashboard />} />

          {/* Sponsorship marketplace */}
          <Route path="/sponsor/signup" element={<SignupSponsor />} />
          <Route path="/sponsors/me" element={<SponsorProfile />} />
          <Route path="/sponsorships" element={<SponsorshipMarketplace />} />

          {/* Scorer console */}
          <Route path="/scorer/dashboard" element={<ScorerDashboard />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" theme="dark" />
    </AuthProvider>
  );
}

export default App;
